import { useEffect, useState } from "react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button } from "~/ui-rtk/components/ui/controls"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import { formatNumber } from "~/ui-rtk/utils/format"
import type { RecommendationRow } from "../types"

interface InitiativeTypeBlockDialogProps {
  isOpen: boolean
  /** Active rows with no initiative type, or CBO campaigns whose spending ad sets lack one. */
  rows: RecommendationRow[]
  onClose: () => void
  /** The user acknowledged the missing types and chose to update budgets anyway; the review may open. */
  onOverride: () => void
}

/**
 * Every active (spending, not paused) campaign and ad set should have an
 * initiative type before a budget update goes through. The allocation
 * splits the target by category, so an unassigned active row is spend the
 * recommender cannot place — it would silently distort every other row's
 * share. Assignment happens in the table's Initiative Type column.
 *
 * The block can be overridden with an explicit acknowledgment. The check
 * reads ad set level assignments, so a campaign the brand did type can still
 * be flagged by one untyped ad set that spent a few cents, and a flag like
 * that must not stop every budget change. Continuing changes nothing about
 * the allocation: untyped rows already sit in the DR share at their current
 * budget, and partly typed ones are already placed by their typed ad sets.
 */
export default function InitiativeTypeBlockDialog({
  isOpen,
  rows,
  onClose,
  onOverride,
}: InitiativeTypeBlockDialogProps) {
  const [acknowledged, setAcknowledged] = useState(false)
  // A fresh open is a fresh decision.
  useEffect(() => {
    if (isOpen) setAcknowledged(false)
  }, [isOpen])

  const untyped = rows.filter(r => r.category === null)
  const untypedBudget = untyped.reduce((s, r) => s + r.currentDailyBudget, 0)
  const partlyTypedCount = rows.length - untyped.length
  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      width={640}
      classes={{
        container:
          "bg-basic-dark-blue border border-basic-blue rounded-xl p-0 text-left max-h-[85vh] flex flex-col",
      }}
    >
      <div className="px-6 py-4 border-b border-basic-blue flex items-center gap-2">
        <ExclamationTriangleIcon className="w-5 h-5 text-states-error shrink-0" />
        <h2 className="text-lg font-semibold text-white">
          Assign initiative types before updating budgets
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        <p className="text-sm text-gray-300">
          {rows.length === 1
            ? "1 active campaign / ad set is"
            : `${rows.length} active campaigns / ad sets are`}{" "}
          spending without an initiative type (no type at all, or some spending
          ad sets without one). The recommender cannot tell whether that spend
          is demand generation or demand capture, so it cannot place it against
          the target.
        </p>
        <p className="text-sm text-gray-400">
          Assign a type in the table&apos;s Initiative Type column, then review
          again.
        </p>

        <ul className="divide-y divide-basic-blue border border-basic-blue rounded-lg">
          {rows.map(row => (
            <li
              key={row.entityId}
              className="px-3 py-2 text-sm flex items-center gap-2 min-w-0"
            >
              <span
                className="text-white truncate min-w-0"
                title={row.entityName}
              >
                {row.entityName}
              </span>
              <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-basic-black rounded shrink-0">
                {row.entityType}
              </span>
              <span className="text-xs text-gray-400 shrink-0">
                {row.channel}
              </span>
              {row.category !== null && row.hasUncategorizedAdsets && (
                <span className="text-xs text-states-warn shrink-0">
                  some spending ad sets have no type
                </span>
              )}
              <span className="ml-auto text-xs text-gray-400 shrink-0">
                {formatNumber(row.currentDailyBudget, { currency: true })}/day
              </span>
            </li>
          ))}
        </ul>

        <div
          data-testid="type-block-override"
          className="rounded-lg border border-states-warn/60 bg-states-warn/10 px-3 py-3 space-y-2"
        >
          <p className="text-sm text-states-warn">
            If these already have a type, or the budgets need to change now, you
            can continue without one:
          </p>
          <ul className="list-disc pl-5 text-xs space-y-0.5">
            {untyped.length > 0 && (
              <li>
                {untyped.length === 1
                  ? "1 row with no type stays at its current budget"
                  : `${untyped.length} rows with no type stay at their current budget`}{" "}
                ({formatNumber(untypedBudget, { currency: true })}/day) and{" "}
                {untyped.length === 1 ? "counts" : "count"} toward the demand
                capture share.
              </li>
            )}
            {partlyTypedCount > 0 && (
              <li>
                {partlyTypedCount === 1
                  ? "1 row with untyped ad sets is"
                  : `${partlyTypedCount} rows with untyped ad sets are`}{" "}
                placed by the type shown in the table, taken from the ad sets
                that have one.
              </li>
            )}
          </ul>
          <label className="flex items-start gap-2 text-sm text-states-warn cursor-pointer">
            <Checkbox
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="checkbox-sm mt-0.5"
            />
            <span>
              I understand and want to update budgets without these initiative
              types.
            </span>
          </label>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-basic-blue flex justify-end gap-3">
        {/* Red: it bypasses a check, and unlike the outlined variant it
            shows its disabled state until the acknowledgment is ticked. */}
        <Button
          variant={{ variant: "solid", color: "red" }}
          size="sm"
          onClick={onOverride}
          disabled={!acknowledged}
        >
          Continue anyway
        </Button>
        <Button
          variant={{ variant: "solid", color: "blue" }}
          size="sm"
          onClick={onClose}
        >
          Back to the table
        </Button>
      </div>
    </Dialog>
  )
}
